import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Settings as SettingsIcon, User, Bell, Users, Shield, Building2, Edit, Plus, X, Trash2, Download, FileText } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { api } from '../utils/api';
import { formatDate, formatPhoneNumber } from '../utils/helpers';

// Helper function to format date from YYYY-MM-DD or YYYYMMDD to MM/DD/YYYY
const formatDisplayDate = (dateStr) => {
  if (!dateStr) return '';
  
  // Handle YYYYMMDD format (no separators)
  if (dateStr.length === 8 && !dateStr.includes('-')) {
    const year = dateStr.substring(0, 4);
    const month = dateStr.substring(4, 6);
    const day = dateStr.substring(6, 8);
    return `${month}/${day}/${year}`;
  }
  
  // Handle YYYY-MM-DD format
  if (dateStr.includes('-')) {
    const [year, month, day] = dateStr.split('-');
    return `${month}/${day}/${year}`;
  }
  
  return dateStr;
};

const Settings = ({ user }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [editingCompany, setEditingCompany] = useState(false);
  const [companyFormData, setCompanyFormData] = useState({
    legal_name: '',
    formation_state: '',
    fein: '',
    naic: '',
    npn: '',
    phone: '',
    extension: '',
    website: '',
    status: 'active',
    principal_office: {
      address: '',
      city: '',
      state: '',
      zip: ''
    },
    registered_agent: {
      name: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      phone: '',
      email: ''
    },
    officers: [],
    name_history: []
  });
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'operations'
  });
  const [passwordChange, setPasswordChange] = useState({
    current: '',
    new: '',
    confirm: ''
  });
  const [notificationSettings, setNotificationSettings] = useState({
    email_enabled: true,
    reminder_7days: true,
    reminder_14days: true,
    reminder_30days: true,
    deficiency_alerts: true,
    overdue_alerts: true
  });

  useEffect(() => {
    loadCompanyInfo();
    if (user.role === 'super_admin') {
      loadUsers();
    }
  }, [user]);

  const loadCompanyInfo = async () => {
    try {
      const data = await api.getEntities();
      // Get the single company entity (LossPay Risk Technologies, Inc.)
      if (data.entities && data.entities.length > 0) {
        const company = data.entities[0];
        setCompanyInfo(company);
        setCompanyFormData({
          legal_name: company.legal_name || '',
          formation_state: company.formation_state || '',
          fein: company.fein || '',
          naic: company.naic || '',
          npn: company.npn || '',
          phone: company.phone || '',
          extension: company.extension || '',
          website: company.website || '',
          status: company.status || 'active',
          principal_office: company.principal_office || {
            address: '',
            city: '',
            state: '',
            zip: ''
          },
          registered_agent: company.registered_agent || {
            name: '',
            address: '',
            city: '',
            state: '',
            zip: '',
            phone: '',
            email: ''
          },
          officers: company.officers || [],
          name_history: company.name_history || []
        });
      }
    } catch (error) {
      console.error('Failed to load company info:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const data = await api.getUsers();
      setUsers(data.users);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (newUser.password.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await api.register(newUser.email, newUser.password, newUser.full_name, newUser.role);
      alert('User created successfully!');
      setNewUser({ email: '', password: '', full_name: '', role: 'operations' });
      loadUsers();
    } catch (error) {
      console.error('Failed to create user:', error);
      alert('Failed to create user. Email may already exist.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (email) => {
    setUserToDelete(email);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;

    setLoading(true);
    try {
      await api.deleteUser(userToDelete);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      loadUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
      alert('Failed to delete user. ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCompanyInfo = async () => {
    if (!companyInfo) return;
    
    setLoading(true);
    try {
      await api.updateEntity(companyInfo.entity_id, companyFormData);
      alert('Company information updated successfully!');
      setEditingCompany(false);
      loadCompanyInfo();
    } catch (error) {
      console.error('Failed to update company info:', error);
      alert('Failed to update company information');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelCompanyEdit = () => {
    setEditingCompany(false);
    loadCompanyInfo(); // Reload to reset form
  };

  const addOfficer = () => {
    setCompanyFormData({
      ...companyFormData,
      officers: [...companyFormData.officers, { name: '', title: '', since: '' }]
    });
  };

  const removeOfficer = (index) => {
    const newOfficers = companyFormData.officers.filter((_, i) => i !== index);
    setCompanyFormData({ ...companyFormData, officers: newOfficers });
  };

  const updateOfficer = (index, field, value) => {
    const newOfficers = [...companyFormData.officers];
    newOfficers[index][field] = value;
    setCompanyFormData({ ...companyFormData, officers: newOfficers });
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordChange.new !== passwordChange.confirm) {
      alert('New passwords do not match');
      return;
    }
    if (passwordChange.new.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }

    alert('Password change functionality would be implemented here');
    setPasswordChange({ current: '', new: '', confirm: '' });
  };

  const handleSaveNotifications = () => {
    localStorage.setItem('notification_settings', JSON.stringify(notificationSettings));
    alert('Notification preferences saved!');
  };

  const handleExport = async (type) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/export/${type}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Export failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}_export.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export data');
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      super_admin: 'bg-rose-100 text-rose-800',
      compliance_admin: 'bg-blue-100 text-blue-800',
      legal_regulatory: 'bg-purple-100 text-purple-800',
      operations: 'bg-green-100 text-green-800',
      executive: 'bg-amber-100 text-amber-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  const formatRoleName = (role) => {
    return role.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  return (
    <div className="space-y-6 fade-in" data-testid="settings-container">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <SettingsIcon className="h-6 w-6 sm:h-8 sm:w-8 text-accent flex-shrink-0" />
        <div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight">Settings</h1>
          <p className="text-muted-foreground mt-2">Manage your account and system preferences</p>
        </div>
      </div>

      <div className="grid gap-6">
        {/* Company Information */}
        <Card data-testid="company-info-section">
          <CardHeader>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <Building2 className="h-5 w-5 text-accent flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <CardTitle className="text-base sm:text-lg">Company Information</CardTitle>
                  <CardDescription className="mt-1">Official details for LossPay Risk Technologies, Inc.</CardDescription>
                </div>
              </div>
              {!editingCompany && companyInfo && (
                <Button onClick={() => setEditingCompany(true)} variant="outline" data-testid="edit-company-button" className="w-full sm:w-auto flex-shrink-0">
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {!companyInfo ? (
              <div className="text-center py-8 text-muted-foreground">Loading company information...</div>
            ) : editingCompany ? (
              /* Edit Mode */
              <div className="space-y-6">
                {/* Basic Information */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Basic Information</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Legal Entity Name</Label>
                      <Input
                        value={companyFormData.legal_name}
                        onChange={(e) => setCompanyFormData({ ...companyFormData, legal_name: e.target.value })}
                        placeholder="Company legal name"
                      />
                    </div>
                    <div>
                      <Label>Formation State</Label>
                      <Input
                        value={companyFormData.formation_state}
                        onChange={(e) => setCompanyFormData({ ...companyFormData, formation_state: e.target.value })}
                        placeholder="e.g., Delaware"
                      />
                    </div>
                    <div>
                      <Label>FEIN</Label>
                      <Input
                        value={companyFormData.fein}
                        onChange={(e) => setCompanyFormData({ ...companyFormData, fein: e.target.value })}
                        placeholder="XX-XXXXXXX"
                      />
                    </div>
                    <div>
                      <Label>NAIC</Label>
                      <Input
                        value={companyFormData.naic}
                        onChange={(e) => setCompanyFormData({ ...companyFormData, naic: e.target.value })}
                        placeholder="NAIC Number"
                      />
                    </div>
                    <div>
                      <Label>NPN</Label>
                      <Input
                        value={companyFormData.npn}
                        onChange={(e) => setCompanyFormData({ ...companyFormData, npn: e.target.value })}
                        placeholder="National Producer Number"
                      />
                    </div>
                    <div>
                      <Label>Status</Label>
                      <Select value={companyFormData.status} onValueChange={(value) => setCompanyFormData({ ...companyFormData, status: value })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="space-y-4 pt-4 border-t">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Contact Information</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="sm:col-span-2">
                      <Label>Phone Number</Label>
                      <Input
                        value={companyFormData.phone}
                        onChange={(e) => setCompanyFormData({ ...companyFormData, phone: formatPhoneNumber(e.target.value) })}
                        placeholder="(555) 555-5555"
                      />
                    </div>
                    <div>
                      <Label>Extension</Label>
                      <Input
                        value={companyFormData.extension}
                        onChange={(e) => setCompanyFormData({ ...companyFormData, extension: e.target.value })}
                        placeholder="1234"
                      />
                    </div>
                    <div className="sm:col-span-3">
                      <Label>Website</Label>
                      <Input
                        value={companyFormData.website}
                        onChange={(e) => setCompanyFormData({ ...companyFormData, website: e.target.value })}
                        placeholder="https://example.com"
                      />
                    </div>
                  </div>
                </div>

                {/* Principal Office */}
                <div className="space-y-4 pt-4 border-t">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Principal Office</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <Label>Address</Label>
                      <Input
                        value={companyFormData.principal_office.address}
                        onChange={(e) => setCompanyFormData({
                          ...companyFormData,
                          principal_office: { ...companyFormData.principal_office, address: e.target.value }
                        })}
                        placeholder="Street address"
                      />
                    </div>
                    <div>
                      <Label>City</Label>
                      <Input
                        value={companyFormData.principal_office.city}
                        onChange={(e) => setCompanyFormData({
                          ...companyFormData,
                          principal_office: { ...companyFormData.principal_office, city: e.target.value }
                        })}
                        placeholder="City"
                      />
                    </div>
                    <div>
                      <Label>State</Label>
                      <Input
                        value={companyFormData.principal_office.state}
                        onChange={(e) => setCompanyFormData({
                          ...companyFormData,
                          principal_office: { ...companyFormData.principal_office, state: e.target.value }
                        })}
                        placeholder="State"
                      />
                    </div>
                    <div>
                      <Label>ZIP Code</Label>
                      <Input
                        value={companyFormData.principal_office.zip}
                        onChange={(e) => setCompanyFormData({
                          ...companyFormData,
                          principal_office: { ...companyFormData.principal_office, zip: e.target.value }
                        })}
                        placeholder="ZIP"
                      />
                    </div>
                  </div>
                </div>

                {/* Registered Agent */}
                <div className="space-y-4 pt-4 border-t">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Registered Agent</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <Label>Agent Name</Label>
                      <Input
                        value={companyFormData.registered_agent.name}
                        onChange={(e) => setCompanyFormData({
                          ...companyFormData,
                          registered_agent: { ...companyFormData.registered_agent, name: e.target.value }
                        })}
                        placeholder="Registered agent name"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label>Address</Label>
                      <Input
                        value={companyFormData.registered_agent.address}
                        onChange={(e) => setCompanyFormData({
                          ...companyFormData,
                          registered_agent: { ...companyFormData.registered_agent, address: e.target.value }
                        })}
                        placeholder="Agent address"
                      />
                    </div>
                    <div>
                      <Label>City</Label>
                      <Input
                        value={companyFormData.registered_agent.city}
                        onChange={(e) => setCompanyFormData({
                          ...companyFormData,
                          registered_agent: { ...companyFormData.registered_agent, city: e.target.value }
                        })}
                      />
                    </div>
                    <div>
                      <Label>State</Label>
                      <Input
                        value={companyFormData.registered_agent.state}
                        onChange={(e) => setCompanyFormData({
                          ...companyFormData,
                          registered_agent: { ...companyFormData.registered_agent, state: e.target.value }
                        })}
                      />
                    </div>
                    <div>
                      <Label>ZIP Code</Label>
                      <Input
                        value={companyFormData.registered_agent.zip}
                        onChange={(e) => setCompanyFormData({
                          ...companyFormData,
                          registered_agent: { ...companyFormData.registered_agent, zip: e.target.value }
                        })}
                      />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input
                        value={companyFormData.registered_agent.phone}
                        onChange={(e) => setCompanyFormData({
                          ...companyFormData,
                          registered_agent: { ...companyFormData.registered_agent, phone: formatPhoneNumber(e.target.value) }
                        })}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label>Email</Label>
                      <Input
                        value={companyFormData.registered_agent.email}
                        onChange={(e) => setCompanyFormData({
                          ...companyFormData,
                          registered_agent: { ...companyFormData.registered_agent, email: e.target.value }
                        })}
                        type="email"
                      />
                    </div>
                  </div>
                </div>

                {/* Officers */}
                <div className="space-y-4 pt-4 border-t">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Officers</h3>
                    <Button onClick={addOfficer} variant="outline" size="sm" className="w-full sm:w-auto">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Officer
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {companyFormData.officers.map((officer, index) => (
                      <div key={index} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 p-3 rounded-lg border">
                        <Input
                          value={officer.name}
                          onChange={(e) => updateOfficer(index, 'name', e.target.value)}
                          placeholder="Officer name"
                        />
                        <Input
                          value={officer.title}
                          onChange={(e) => updateOfficer(index, 'title', e.target.value)}
                          placeholder="Title (e.g., CEO)"
                        />
                        <Input
                          value={officer.since}
                          onChange={(e) => updateOfficer(index, 'since', e.target.value)}
                          placeholder="Since (MM/DD/YYYY)"
                        />
                        <Button onClick={() => removeOfficer(index)} variant="ghost" size="sm">
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-4">
                  <Button onClick={handleSaveCompanyInfo} disabled={loading} data-testid="save-company-button" className="w-full sm:w-auto">
                    {loading ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button onClick={handleCancelCompanyEdit} variant="outline" disabled={loading} className="w-full sm:w-auto">
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              /* View Mode */
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div>
                    <Label className="text-sm text-muted-foreground">Legal Entity Name</Label>
                    <p className="text-base font-medium mt-1">{companyInfo.legal_name}</p>
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">Formation State</Label>
                    <p className="text-base font-medium mt-1">{companyInfo.formation_state || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">Status</Label>
                    <div className="mt-1">
                      <Badge variant={companyInfo.status === 'active' ? 'default' : 'secondary'}>
                        {companyInfo.status}
                      </Badge>
                    </div>
                  </div>
                  {companyInfo.fein && (
                    <div>
                      <Label className="text-sm text-muted-foreground">FEIN</Label>
                      <p className="text-base font-medium mt-1 font-mono">{companyInfo.fein}</p>
                    </div>
                  )}
                  {companyInfo.naic && (
                    <div>
                      <Label className="text-sm text-muted-foreground">NAIC</Label>
                      <p className="text-base font-medium mt-1 font-mono">{companyInfo.naic}</p>
                    </div>
                  )}
                  {companyInfo.npn && (
                    <div>
                      <Label className="text-sm text-muted-foreground">NPN</Label>
                      <p className="text-base font-medium mt-1 font-mono">{companyInfo.npn}</p>
                    </div>
                  )}
                  {companyInfo.phone && (
                    <div>
                      <Label className="text-sm text-muted-foreground">Phone</Label>
                      <p className="text-base font-medium mt-1">
                        {formatPhoneNumber(companyInfo.phone)}
                        {companyInfo.extension && <span className="text-muted-foreground"> ext. {companyInfo.extension}</span>}
                      </p>
                    </div>
                  )}
                  {companyInfo.website && (
                    <div className="sm:col-span-2">
                      <Label className="text-sm text-muted-foreground">Website</Label>
                      <p className="text-base font-medium mt-1">
                        <a href={companyInfo.website} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
                          {companyInfo.website}
                        </a>
                      </p>
                    </div>
                  )}
                </div>

                {companyInfo.principal_office && companyInfo.principal_office.address && (
                  <div className="pt-4 border-t">
                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Principal Office</h3>
                    <p className="text-base">
                      {companyInfo.principal_office.address}<br />
                      {companyInfo.principal_office.city}, {companyInfo.principal_office.state} {companyInfo.principal_office.zip}
                    </p>
                  </div>
                )}

                {companyInfo.registered_agent && companyInfo.registered_agent.name && (
                  <div className="pt-4 border-t">
                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Registered Agent</h3>
                    <div className="space-y-2">
                      <p className="text-base font-medium">{companyInfo.registered_agent.name}</p>
                      {companyInfo.registered_agent.address && (
                        <p className="text-sm text-muted-foreground">
                          {companyInfo.registered_agent.address}<br />
                          {companyInfo.registered_agent.city}, {companyInfo.registered_agent.state} {companyInfo.registered_agent.zip}
                        </p>
                      )}
                      {companyInfo.registered_agent.phone && (
                        <p className="text-sm">{formatPhoneNumber(companyInfo.registered_agent.phone)}</p>
                      )}
                      {companyInfo.registered_agent.email && (
                        <p className="text-sm">{companyInfo.registered_agent.email}</p>
                      )}
                    </div>
                  </div>
                )}

                {companyInfo.officers && companyInfo.officers.length > 0 && (
                  <div className="pt-4 border-t">
                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Officers</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {companyInfo.officers.map((officer, index) => (
                        <div key={index} className="p-3 rounded-lg border">
                          <p className="font-medium">{officer.name}</p>
                          <p className="text-sm text-muted-foreground">{officer.title}</p>
                          {officer.since && <p className="text-xs text-muted-foreground mt-1">Since {formatDisplayDate(officer.since)}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {companyInfo.name_history && companyInfo.name_history.length > 0 && (
                  <div className="pt-4 border-t">
                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Name History</h3>
                    <div className="space-y-2">
                      {companyInfo.name_history.map((entry, index) => (
                        <div key={index} className="text-sm">
                          <span className="font-medium">{entry.name}</span>
                          <span className="text-muted-foreground"> ({formatDisplayDate(entry.from)} - {entry.to ? formatDisplayDate(entry.to) : 'Present'})</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* User Profile */}
        <Card data-testid="profile-section">
          <CardHeader>
            <div className="flex items-center gap-3">
              <User className="h-5 w-5 text-accent" />
              <CardTitle>Profile Information</CardTitle>
            </div>
            <CardDescription>Your account details and role</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <Label className="text-sm text-muted-foreground">Full Name</Label>
                <p className="text-lg font-medium mt-1">{user.full_name}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">Email</Label>
                <p className="text-lg font-medium mt-1">{user.email}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">Role</Label>
                <div className="mt-1">
                  <Badge className={getRoleBadgeColor(user.role)}>
                    {formatRoleName(user.role)}
                  </Badge>
                </div>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">Account Status</Label>
                <div className="mt-1">
                  <Badge className="bg-emerald-100 text-emerald-800">Active</Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Password Change */}
        <Card data-testid="password-section">
          <CardHeader>
            <div className="flex items-center gap-3">
              <Shield className="h-5 w-5 text-accent" />
              <CardTitle>Change Password</CardTitle>
            </div>
            <CardDescription>Update your account password</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
              <div className="space-y-2">
                <Label htmlFor="current_password">Current Password</Label>
                <Input
                  id="current_password"
                  type="password"
                  value={passwordChange.current}
                  onChange={(e) => setPasswordChange({ ...passwordChange, current: e.target.value })}
                  placeholder="Enter current password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new_password">New Password</Label>
                <Input
                  id="new_password"
                  type="password"
                  value={passwordChange.new}
                  onChange={(e) => setPasswordChange({ ...passwordChange, new: e.target.value })}
                  placeholder="Enter new password (min 6 characters)"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm_password">Confirm New Password</Label>
                <Input
                  id="confirm_password"
                  type="password"
                  value={passwordChange.confirm}
                  onChange={(e) => setPasswordChange({ ...passwordChange, confirm: e.target.value })}
                  placeholder="Confirm new password"
                />
              </div>
              <Button type="submit" className="btn-hover">
                Update Password
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Notification Settings */}
        <Card data-testid="notifications-section">
          <CardHeader>
            <div className="flex items-center gap-3">
              <Bell className="h-5 w-5 text-accent" />
              <CardTitle>Notification Preferences</CardTitle>
            </div>
            <CardDescription>Configure how and when you receive alerts</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="font-medium">Email Notifications</Label>
                  <p className="text-sm text-muted-foreground">Receive notifications via email</p>
                </div>
                <input
                  type="checkbox"
                  checked={notificationSettings.email_enabled}
                  onChange={(e) => setNotificationSettings({ ...notificationSettings, email_enabled: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300"
                />
              </div>

              <div className="border-t pt-4 space-y-3">
                <Label className="font-medium">Deadline Reminders</Label>
                <div className="space-y-2 ml-4">
                  <div className="flex items-center justify-between">
                    <Label className="font-normal text-sm">7 days before due date</Label>
                    <Checkbox
                      checked={notificationSettings.reminder_7days}
                      onCheckedChange={(checked) => setNotificationSettings({ ...notificationSettings, reminder_7days: checked })}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="font-normal text-sm">14 days before due date</Label>
                    <Checkbox
                      checked={notificationSettings.reminder_14days}
                      onCheckedChange={(checked) => setNotificationSettings({ ...notificationSettings, reminder_14days: checked })}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="font-normal text-sm">30 days before due date</Label>
                    <Checkbox
                      checked={notificationSettings.reminder_30days}
                      onCheckedChange={(checked) => setNotificationSettings({ ...notificationSettings, reminder_30days: checked })}
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4 space-y-3">
                <Label className="font-medium">Alert Types</Label>
                <div className="space-y-2 ml-4">
                  <div className="flex items-center justify-between">
                    <Label className="font-normal text-sm">Deficiency alerts</Label>
                    <Checkbox
                      checked={notificationSettings.deficiency_alerts}
                      onCheckedChange={(checked) => setNotificationSettings({ ...notificationSettings, deficiency_alerts: checked })}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="font-normal text-sm">Overdue item alerts</Label>
                    <Checkbox
                      checked={notificationSettings.overdue_alerts}
                      onCheckedChange={(checked) => setNotificationSettings({ ...notificationSettings, overdue_alerts: checked })}
                    />
                  </div>
                </div>
              </div>

              <Button onClick={handleSaveNotifications} className="btn-hover mt-4">
                Save Notification Preferences
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* User Management (Admin Only) */}
        {user.role === 'super_admin' && (
          <Card data-testid="user-management-section">
            <CardHeader>
              <div className="flex items-center gap-3">
                <Users className="h-5 w-5 text-accent" />
                <CardTitle>User Management</CardTitle>
              </div>
              <CardDescription>Create and manage user accounts</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Create New User Form */}
                <div className="border rounded-lg p-4 bg-muted/30">
                  <h3 className="font-semibold mb-4">Create New User</h3>
                  <form onSubmit={handleCreateUser} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="new_email">Email</Label>
                        <Input
                          id="new_email"
                          type="email"
                          value={newUser.email}
                          onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                          placeholder="user@losspay.com"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="new_full_name">Full Name</Label>
                        <Input
                          id="new_full_name"
                          value={newUser.full_name}
                          onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                          placeholder="John Doe"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="new_password">Password</Label>
                        <Input
                          id="new_password"
                          type="password"
                          value={newUser.password}
                          onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                          placeholder="Min 6 characters"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="new_role">Role</Label>
                        <Select value={newUser.role} onValueChange={(value) => setNewUser({ ...newUser, role: value })}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="super_admin">Super Admin</SelectItem>
                            <SelectItem value="compliance_admin">Compliance Admin</SelectItem>
                            <SelectItem value="legal_regulatory">Legal & Regulatory</SelectItem>
                            <SelectItem value="operations">Operations</SelectItem>
                            <SelectItem value="executive">Executive</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <Button type="submit" disabled={loading} className="btn-hover">
                      {loading ? 'Creating...' : 'Create User'}
                    </Button>
                  </form>
                </div>

                {/* Users List */}
                <div>
                  <h3 className="font-semibold mb-4">Existing Users</h3>
                  <div className="space-y-3">
                    {users.map((u, idx) => (
                      <div key={idx} className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors" data-testid={`user-row-${u.email}`}>
                        <div className="flex items-center gap-4 flex-1 min-w-0">
                          <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                            <User className="h-5 w-5 text-accent" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{u.full_name}</p>
                            <p className="text-sm text-muted-foreground truncate">{u.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                          <Badge className={getRoleBadgeColor(u.role)}>
                            {formatRoleName(u.role)}
                          </Badge>
                          {u.email !== user.email ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteUser(u.email)}
                              disabled={loading}
                              className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                              data-testid={`delete-user-${u.email}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          ) : (
                            <span className="text-xs text-muted-foreground px-3 py-1">(You)</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Export Data */}
        <Card data-testid="export-data-section">
          <CardHeader>
            <div className="flex items-center gap-3">
              <Download className="h-5 w-5 text-accent" />
              <div>
                <CardTitle>Export Data</CardTitle>
                <CardDescription>Download compliance data as CSV files for backup or analysis</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <Card className="border-2 hover:border-accent transition-colors">
                <CardContent className="p-5">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <FileText className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-sm mb-1">Licenses</h4>
                      <p className="text-xs text-muted-foreground">All insurance licenses and permits</p>
                    </div>
                  </div>
                  <Button 
                    onClick={() => handleExport('licenses')} 
                    size="sm" 
                    variant="outline" 
                    className="w-full"
                    data-testid="export-licenses-button"
                  >
                    <Download className="h-3 w-3 mr-2" />
                    Export Licenses
                  </Button>
                </CardContent>
              </Card>

              <Card className="border-2 hover:border-accent transition-colors">
                <CardContent className="p-5">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                      <FileText className="h-5 w-5 text-green-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-sm mb-1">DBAs</h4>
                      <p className="text-xs text-muted-foreground">Doing Business As registrations</p>
                    </div>
                  </div>
                  <Button 
                    onClick={() => handleExport('dbas')} 
                    size="sm" 
                    variant="outline" 
                    className="w-full"
                    data-testid="export-dbas-button"
                  >
                    <Download className="h-3 w-3 mr-2" />
                    Export DBAs
                  </Button>
                </CardContent>
              </Card>

              <Card className="border-2 hover:border-accent transition-colors">
                <CardContent className="p-5">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <FileText className="h-5 w-5 text-purple-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-sm mb-1">State Requirements</h4>
                      <p className="text-xs text-muted-foreground">All state compliance requirements</p>
                    </div>
                  </div>
                  <Button 
                    onClick={() => handleExport('state-requirements')} 
                    size="sm" 
                    variant="outline" 
                    className="w-full"
                    data-testid="export-state-requirements-button"
                  >
                    <Download className="h-3 w-3 mr-2" />
                    Export Requirements
                  </Button>
                </CardContent>
              </Card>

              <Card className="border-2 hover:border-accent transition-colors">
                <CardContent className="p-5">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                      <FileText className="h-5 w-5 text-orange-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-sm mb-1">Filings</h4>
                      <p className="text-xs text-muted-foreground">Regulatory filings and submissions</p>
                    </div>
                  </div>
                  <Button 
                    onClick={() => handleExport('filings')} 
                    size="sm" 
                    variant="outline" 
                    className="w-full"
                    data-testid="export-filings-button"
                  >
                    <Download className="h-3 w-3 mr-2" />
                    Export Filings
                  </Button>
                </CardContent>
              </Card>

              <Card className="border-2 hover:border-accent transition-colors">
                <CardContent className="p-5">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                      <FileText className="h-5 w-5 text-red-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-sm mb-1">Registered Agents</h4>
                      <p className="text-xs text-muted-foreground">State-specific registered agents</p>
                    </div>
                  </div>
                  <Button 
                    onClick={() => handleExport('registered-agents')} 
                    size="sm" 
                    variant="outline" 
                    className="w-full"
                    data-testid="export-agents-button"
                  >
                    <Download className="h-3 w-3 mr-2" />
                    Export Agents
                  </Button>
                </CardContent>
              </Card>

              <Card className="border-2 hover:border-accent transition-colors">
                <CardContent className="p-5">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center flex-shrink-0">
                      <FileText className="h-5 w-5 text-yellow-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-sm mb-1">Tasks</h4>
                      <p className="text-xs text-muted-foreground">Compliance tasks and workflows</p>
                    </div>
                  </div>
                  <Button 
                    onClick={() => handleExport('tasks')} 
                    size="sm" 
                    variant="outline" 
                    className="w-full"
                    data-testid="export-tasks-button"
                  >
                    <Download className="h-3 w-3 mr-2" />
                    Export Tasks
                  </Button>
                </CardContent>
              </Card>
            </div>

            <div className="mt-6 p-4 bg-muted/50 rounded-lg">
              <p className="text-sm text-muted-foreground">
                <strong>Note:</strong> CSV files will be downloaded to your device and can be opened in Excel, Google Sheets, or any spreadsheet application. 
                All dates are in YYYY-MM-DD format. Exports include all records currently in the system.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* System Information */}
        <Card>
          <CardHeader>
            <CardTitle>System Information</CardTitle>
            <CardDescription>Platform details and version</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
              <div>
                <Label className="text-muted-foreground">Platform Version</Label>
                <p className="font-medium mt-1">1.0.0</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Last Updated</Label>
                <p className="font-medium mt-1">{formatDate(new Date())}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Environment</Label>
                <p className="font-medium mt-1">Production</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Delete User Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete user <strong>{userToDelete}</strong>? 
              This action cannot be undone and will permanently remove the user from the system.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setUserToDelete(null)} disabled={loading}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDeleteUser}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={loading}
              data-testid="confirm-delete-user-button"
            >
              {loading ? 'Deleting...' : 'Delete User'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Settings;